import React, { useEffect, useState, useRef } from "react";
import {
  collection,
  onSnapshot,
  query,
  orderBy,
  addDoc,
  serverTimestamp,
  doc,
  getDoc,
  setDoc,
} from "firebase/firestore";
import { ref, uploadBytes, getDownloadURL } from "firebase/storage";
import { db, storage } from "../firebase";
import { motion, AnimatePresence } from "framer-motion";

export default function AdminChat() {
  const [allMessages, setAllMessages] = useState([]);
  const [viewingNames, setViewingNames] = useState({});
  const [selectedEmail, setSelectedEmail] = useState(null);
  const [editName, setEditName] = useState("");
  const [input, setInput] = useState("");
  const [uploadFile, setUploadFile] = useState(null);
  const bottomRef = useRef(null);

  useEffect(() => {
    const q = query(collection(db, "messages"), orderBy("timestamp"));
    const unsub = onSnapshot(q, (snapshot) => {
      const data = snapshot.docs.map((doc) => ({ id: doc.id, ...doc.data() }));
      setAllMessages(data);
    });
    return () => unsub();
  }, []);

  useEffect(() => {
    const fetchViewingNames = async () => {
      const snap = await getDoc(doc(db, "chatUsers", "viewingNames"));
      if (snap.exists()) {
        setViewingNames(snap.data());
      }
    };
    fetchViewingNames();
  }, []);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [allMessages, selectedEmail]);

  const uniqueUsers = [
    ...new Set(
      allMessages
        .map((msg) => msg.senderEmail)
        .filter((e) => e !== "admin@labdevelopers.com")
    ),
  ];

  const filteredMessages = allMessages.filter(
    (msg) =>
      msg.senderEmail === selectedEmail || msg.receiverEmail === selectedEmail
  );

  const handleSend = async () => {
    if (!input.trim() && !uploadFile) return;

    let fileUrl = "";
    if (uploadFile) {
      const safeName = uploadFile.name.replace(/\s+/g, "_");
      const fileRef = ref(storage, `chatUploads/${Date.now()}_${safeName}`);
      await uploadBytes(fileRef, uploadFile);
      fileUrl = await getDownloadURL(fileRef);
    }

    await addDoc(collection(db, "messages"), {
      text: input,
      senderEmail: "admin@labdevelopers.com",
      senderName: "Admin",
      receiverEmail: selectedEmail,
      imageUrl: fileUrl.match(/\.(jpg|jpeg|png|gif)$/i) ? fileUrl : "",
      fileUrl: !fileUrl.match(/\.(jpg|jpeg|png|gif)$/i) ? fileUrl : "",
      timestamp: serverTimestamp(),
    });

    setInput("");
    setUploadFile(null);
  };

  const saveViewingName = async () => {
    const updated = { ...viewingNames, [selectedEmail]: editName };
    await setDoc(doc(db, "chatUsers", "viewingNames"), updated);
    setViewingNames(updated);
    setEditName("");
  };

  return (
    <div className="h-screen flex flex-col bg-[#f8fafc] text-slate-100 font-sans selection:bg-blue-500/10">
      {/* Header */}
      <header className="bg-slate-900 text-white px-10 py-8 flex justify-between items-center shadow-[0_20px_40px_rgba(0,0,0,0.05)] relative overflow-hidden shrink-0">
        <div className="absolute inset-0 bg-mesh opacity-20 pointer-events-none" />
        <div className="relative z-10">
          <h1 className="text-2xl font-black tracking-tighter uppercase">LAB <span className="text-blue-500">DEVS</span> <span className="text-slate-400 ml-4 text-xs tracking-widest">Command Center</span></h1>
        </div>
        <div className="relative z-10 flex items-center gap-4">
          <div className="w-3 h-3 bg-green-500 rounded-full animate-pulse" />
          <span className="text-[10px] font-black uppercase tracking-[0.3em] text-slate-400">System Live</span>
        </div>
      </header>

      {/* Main Layout */}
      <div className="flex flex-1 overflow-hidden">
        {/* Sidebar */}
        <aside className="w-[350px] bg-slate-900/40 border-r border-white/5 overflow-y-auto p-8 custom-scrollbar shrink-0 shadow-inner">
          <h2 className="text-[10px] font-black text-slate-400 uppercase tracking-[0.4em] mb-10">Active Nodes</h2>
          <div className="space-y-4">
            {uniqueUsers.map((email) => (
              <motion.div
                whileHover={{ x: 5 }}
                key={email}
                onClick={() => {
                  setSelectedEmail(email);
                  setEditName(viewingNames[email] || "");
                }}
                className={`p-6 rounded-[2rem] cursor-pointer transition-all border ${selectedEmail === email
                    ? "bg-slate-900 text-white border-slate-900 shadow-xl shadow-slate-900/10"
                    : "bg-[#020617] text-slate-400 border-white/5 hover:bg-slate-900/40 hover:border-white/10"
                  }`}
              >
                <div className="flex items-center gap-4">
                  <div className={`w-2 h-2 rounded-full ${selectedEmail === email ? "bg-blue-500" : "bg-slate-300"}`} />
                  <div className="overflow-hidden">
                    <p className="text-xs font-black uppercase tracking-tighter truncate">{viewingNames[email] || email.split('@')[0]}</p>
                    <p className={`text-[8px] font-black uppercase tracking-[0.2em] mt-1 ${selectedEmail === email ? "text-slate-400" : "text-slate-400"}`}>{email}</p>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </aside>

        {/* Chat Area */}
        <main className="flex-1 flex flex-col bg-[#020617]/30 relative">
          <div className="absolute inset-0 bg-mesh opacity-5 pointer-events-none" />

          {selectedEmail ? (
            <>
              {/* Chat Header */}
              <div className="flex items-center justify-between px-10 py-8 bg-slate-900/40/80 backdrop-blur-md border-b border-white/5 z-10">
                <div>
                  <h2 className="text-xl font-black text-white tracking-tighter uppercase">
                    Session: {viewingNames[selectedEmail] || selectedEmail}
                  </h2>
                  <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest mt-1">Status: Active Link</p>
                </div>
                <div className="flex gap-4 items-center">
                  <input
                    type="text"
                    placeholder="Rename identity..."
                    value={editName}
                    onChange={(e) => setEditName(e.target.value)}
                    className="bg-[#020617] border border-white/5 rounded-full px-6 py-2 text-[10px] font-black uppercase tracking-widest focus:outline-none focus:border-blue-500 min-w-[200px]"
                  />
                  <button
                    onClick={saveViewingName}
                    className="px-6 py-2 bg-slate-900 text-white rounded-full text-[10px] font-black uppercase tracking-widest hover:bg-slate-800 transition shadow-sm"
                  >
                    Commit
                  </button>
                </div>
              </div>

              {/* Messages */}
              <div className="flex-1 overflow-y-auto p-10 space-y-8 custom-scrollbar">
                {filteredMessages.map((msg, i) => (
                  <motion.div
                    initial={{ opacity: 0, scale: 0.95 }}
                    animate={{ opacity: 1, scale: 1 }}
                    key={i}
                    className={`max-w-[70%] p-8 rounded-[3rem] shadow-sm relative ${msg.senderEmail === "admin@labdevelopers.com"
                        ? "bg-slate-900 text-white ml-auto rounded-tr-none"
                        : "bg-slate-900/40 text-white border border-white/5 rounded-tl-none"
                      }`}
                  >
                    <div className="flex items-center justify-between mb-4">
                      <p className={`text-[8px] font-black uppercase tracking-[0.3em] ${msg.senderEmail === "admin@labdevelopers.com" ? "text-slate-400" : "text-blue-600"}`}>
                        {msg.senderName}
                      </p>
                      <p className="text-[8px] font-black uppercase tracking-[0.2em] text-slate-400">
                        {msg.timestamp?.toDate().toLocaleTimeString()}
                      </p>
                    </div>
                    <p className="text-sm font-medium leading-relaxed">{msg.text}</p>
                    {msg.imageUrl && (
                      <img
                        src={msg.imageUrl}
                        alt="upload"
                        className="w-64 rounded-2xl mt-6 border border-white/10 shadow-lg"
                      />
                    )}
                    {msg.fileUrl && (
                      <a
                        href={msg.fileUrl}
                        target="_blank"
                        rel="noreferrer"
                        className="text-[10px] font-black uppercase tracking-[0.2em] text-blue-500 mt-6 inline-block hover:underline"
                      >
                        📄 View Manifest Asset
                      </a>
                    )}
                  </motion.div>
                ))}
                <div ref={bottomRef} />
              </div>

              {/* Input Area */}
              <div className="p-10 bg-slate-900/40 border-t border-white/5 z-10">
                <div className="flex gap-4 items-center bg-[#020617] p-3 rounded-[3rem] border border-white/5">
                  <input
                    type="text"
                    value={input}
                    onChange={(e) => setInput(e.target.value)}
                    onKeyDown={(e) => e.key === "Enter" && handleSend()}
                    placeholder="Transmit protocol message..."
                    className="flex-1 bg-transparent px-8 py-3 text-sm font-medium focus:outline-none placeholder:text-slate-400"
                  />
                  <div className="relative">
                    <input
                      type="file"
                      id="adminUpload"
                      onChange={(e) => setUploadFile(e.target.files[0])}
                      className="hidden"
                    />
                    <label htmlFor="adminUpload" className="cursor-pointer w-12 h-12 bg-slate-900/40 border border-white/5 rounded-full flex items-center justify-center hover:bg-slate-200 transition text-xl shadow-sm">
                      📎
                    </label>
                  </div>
                  <button
                    onClick={handleSend}
                    className="px-10 py-3 bg-slate-900 hover:bg-slate-800 text-white rounded-full font-black text-[10px] uppercase tracking-widest transition shadow-lg"
                  >
                    Transmit
                  </button>
                </div>
                {uploadFile && (
                  <div className="mt-4 flex items-center justify-between px-8 py-2 bg-blue-50/50 rounded-xl border border-blue-100">
                    <span className="text-[10px] font-black text-blue-600 uppercase tracking-widest truncate max-w-sm">Attachment: {uploadFile.name}</span>
                    <button onClick={() => setUploadFile(null)} className="text-blue-600 font-black text-[10px]">Remove</button>
                  </div>
                )}
              </div>
            </>
          ) : (
            <div className="flex-1 flex flex-col items-center justify-center text-center p-20">
              <div className="w-32 h-32 bg-slate-100 rounded-[4rem] flex items-center justify-center text-6xl mb-10">🏁</div>
              <h2 className="text-4xl font-black text-white uppercase tracking-tighter mb-6">Select Protocol Node</h2>
              <p className="text-xl text-slate-400 max-w-md font-medium lowercase tracking-tight">select a client identity from the left panel to initiate a secure engineering communication session.</p>
            </div>
          )}
        </main>
      </div>
    </div>
  );
}
